import { FormEvent, useCallback, useRef, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import {
  Box,
  Button,
  Footnote,
  Side,
  TabBar,
  Text,
  TextButton,
} from '@revolut-internal/ui-kit'

import { InputWithRifm } from 'components/InputWithRifm/InputWithRifm'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { useAlertingActionsQuery } from 'modules/api/alerting/queries'
import { Security } from 'modules/security/security'
import { AlertingPolicyCreateRequest } from 'types'

import { useQueueHealthPolicy } from '../QueueHealthPolicyProvider'
import { AlertingPolicyForm } from '../types'
import { convertPolicyToForm } from '../utils'

import { EMPTY_POLICY, FORM_ID, FormState } from './contst'
import { QueueHealthPolicyFormActions } from './QueueHealthPolicyFormActions'
import { QueueHealthPolicyFormMetrics } from './QueueHealthPolicyFormMetrics'

export type QueueHealthPolicyFormProps = {
  policy?: AlertingPolicyCreateRequest | null
  isPending: boolean
  onSubmit: (formValues: AlertingPolicyForm) => void
  onTriggerThresholdDeletion: VoidFunction
}

const CLICK_TO_DESCRIPTION_BLUR_DELAY_MS = 100
export const slackChannelVariableOptions = {
  accept: /[0-9a-zA-Z-_]/g,
  format: (value: string) => {
    const clearedValue = value.replace(/[^0-9a-zA-Z-_]/g, '')

    return `#${clearedValue}`
  },
}

export const QueueHealthPolicyForm = ({
  policy,
  isPending,
  onSubmit,
  onTriggerThresholdDeletion,
}: QueueHealthPolicyFormProps) => {
  const tabsRef = useRef<HTMLDivElement>(null)
  const [isChannelInputFocused, setIsChannelInputFocused] = useState(false)
  const [formState, setFormState] = useState<FormState | null>('metrics')

  const { scrollRef } = useQueueHealthPolicy()
  const methods = useForm<AlertingPolicyForm>({
    defaultValues: policy ? convertPolicyToForm(policy) : EMPTY_POLICY,
  })
  const { data: actions, isPending: isActionsPending } = useAlertingActionsQuery()

  const hasHiddenMetricValidationErrors =
    formState === FormState.Actions && Boolean(methods.getFieldState('thresholds').error)
  const hasHiddenActionsValidationErrors =
    formState === FormState.Metrics &&
    Boolean(methods.getFieldState('alertingPolicyActions').error)

  const handleScrollToTabs = useCallback(() => {
    if (hasHiddenMetricValidationErrors || hasHiddenActionsValidationErrors) {
      scrollRef?.current?.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [hasHiddenMetricValidationErrors, hasHiddenActionsValidationErrors, scrollRef])

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      methods.handleSubmit(onSubmit)(event)
      handleScrollToTabs()
    },
    [methods, onSubmit, handleScrollToTabs],
  )

  const isReadonly = !Security.canCreatePolicy

  return (
    <>
      <FormProvider {...methods}>
        <Box use="form" id={FORM_ID} onSubmit={handleSubmit}>
          <Controller
            name="slackChannel"
            control={methods.control}
            rules={{
              required: 'Field is required',
              validate: {
                required: value =>
                  !value.replace('#', '').length ? `Field is required` : undefined,
                maxLength: value =>
                  value.replace('#', '').length > 80
                    ? `Max length: 80 characters`
                    : undefined,
              },
            }}
            render={({ field, fieldState }) => (
              <InputWithRifm
                label="Slack channel to notify"
                invalid={!!fieldState.error}
                errorMessage={fieldState.error?.message}
                disabled={isReadonly}
                {...field}
                options={slackChannelVariableOptions}
                description={
                  isChannelInputFocused && (
                    <Text>
                      You must add &quot;RTM&quot; app to the channel.{' '}
                      <TextButton
                        use="a"
                        href="https://revolut.atlassian.net/wiki/spaces/CX/pages/5294492109/How+to+allow+incident+alerts+in+your+Slack+channel"
                        target="_blank"
                      >
                        Learn more
                      </TextButton>
                    </Text>
                  )
                }
                onFocus={() => setIsChannelInputFocused(true)}
                onBlur={() =>
                  setTimeout(
                    () => setIsChannelInputFocused(false),
                    CLICK_TO_DESCRIPTION_BLUR_DELAY_MS,
                  )
                }
              />
            )}
          />

          {Security.canViewIncidentActions && (
            <TabBar
              ref={tabsRef}
              variant="segmented fit"
              mt="s-16"
              value={formState}
              onChange={setFormState}
            >
              <TabBar.Item to="metrics" hasDot={hasHiddenMetricValidationErrors}>
                Metrics
              </TabBar.Item>
              <TabBar.Item to="actions" hasDot={hasHiddenActionsValidationErrors}>
                Actions
              </TabBar.Item>
            </TabBar>
          )}

          {/* to keep the form validation */}
          <Box
            display={formState === FormState.Metrics ? 'block' : 'none'}
            hidden={formState !== FormState.Metrics}
          >
            <QueueHealthPolicyFormMetrics
              isReadonly={isReadonly}
              onTriggerThresholdDeletion={onTriggerThresholdDeletion}
            />
          </Box>
          {Security.canViewIncidentActions && (
            <Box
              display={formState === FormState.Actions ? 'block' : 'none'}
              hidden={formState !== FormState.Actions}
            >
              <QueueHealthPolicyFormActions
                actions={actions}
                isActionsPending={isActionsPending}
                isReadonly={isReadonly}
              />
            </Box>
          )}
        </Box>
      </FormProvider>

      {!isReadonly && (
        <Side.Actions>
          <Tooltip
            disabled={
              !hasHiddenMetricValidationErrors && !hasHiddenActionsValidationErrors
            }
            message={`${hasHiddenMetricValidationErrors ? 'Metrics' : 'Actions'} tab has pending errors`}
          >
            <Button
              type="submit"
              form={FORM_ID}
              pending={isPending}
              disabled={!methods.formState.isDirty}
              elevated
            >
              Save
            </Button>
          </Tooltip>
          {policy && (
            <Footnote>
              <Text color="warning">
                Policy changes automatically resolve ongoing incident
              </Text>
            </Footnote>
          )}
        </Side.Actions>
      )}
    </>
  )
}
